import type { FunctionComponent } from 'react'
import React, { useContext as useContextCore, useMemo, useCallback } from 'react'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { FormSaveCancelButtons } from '@ztechapps/webui.form'
import { QuickAddTaskForm } from './QuickAddTaskForm'
import type { QuickAddTaskFormValues } from './useQuickAddTask'
import { useQuickAddTask } from './useQuickAddTask'

export type QuickAddTaskComponent = {
  formInput: FunctionComponent
  saveCancelButtons: FunctionComponent<SaveCancelButtonsProps>
}

const defaultComponents: QuickAddTaskComponent = {
  formInput: QuickAddTaskForm,
  saveCancelButtons: FormSaveCancelButtons,
}

export type QuickAddTaskProviderProps = {
  onSubmit?: (values: QuickAddTaskFormValues) => Promise<void>
  onReset?: () => void
  children: React.ReactNode
  initialValues?: QuickAddTaskFormValues
  components?: QuickAddTaskComponent
  onCloseMenu?: () => void
}

export type QuickAddTaskContextType = {
  validate: (value: QuickAddTaskFormValues) => any
} & Omit<QuickAddTaskProviderProps, 'children'>

const context = React.createContext<QuickAddTaskContextType>({} as QuickAddTaskContextType)
const CoreProvider = context.Provider

const validate = (value: QuickAddTaskFormValues) => {
  const errors: any = {}
  if (!value?.project?._id) {
    errors.project = 'Required'
  }
  if (!value?.name) {
    errors.name = 'Required'
  }
  return errors
}

export const QuickAddTaskProvider = ({
  children,
  components,
  onSubmit,
  initialValues,
  onCloseMenu,
  ...props
}: QuickAddTaskProviderProps) => {
  initialValues = initialValues ?? { name: '', project: null }

  const { onSubmit: defaultOnSubmit } = useQuickAddTask({
    onSuccess: useCallback(() => {
      onCloseMenu?.()
    }, [onCloseMenu]),
  })

  onSubmit = onSubmit ?? defaultOnSubmit

  const mergedComponents = useMemo(() => ({ ...defaultComponents, ...components }), [components])

  const contextValue = useMemo(
    () => ({ initialValues, validate, components: mergedComponents, onSubmit, onCloseMenu, ...props }),
    [initialValues, mergedComponents, onSubmit, onCloseMenu, props]
  )

  return <CoreProvider value={contextValue}>{children}</CoreProvider>
}

export const useQuickAddTaskContext = () => useContextCore(context)
